/**
 * Side-effect: attach correlation headers to bare `axios` calls that target VITE_BASE_URL
 * (apiClient uses its own instance; this covers `import axios from "axios"` usages).
 */
import axios from "axios";

import {
  correlationHeaders,
  syncCorrelationIdFromAxiosHeaders,
} from "@/utils/correlationId";

const API_BASE = import.meta.env.VITE_BASE_URL as string | undefined;
const API_BASE_URL = (() => {
  if (!API_BASE) {
    return null;
  }
  try {
    return new URL(API_BASE, window.location.origin);
  } catch {
    return null;
  }
})();

const hasPathPrefix = (pathname: string, basePath: string): boolean => {
  if (pathname === basePath) {
    return true;
  }
  const normalizedBasePath = basePath.endsWith("/")
    ? basePath
    : `${basePath}/`;
  return pathname.startsWith(normalizedBasePath);
};

axios.interceptors.request.use((config) => {
  const url = config.url ?? "";
  if (!API_BASE || typeof url !== "string") {
    return config;
  }

  let shouldAttachCorrelationHeaders = false;
  if (API_BASE_URL) {
    try {
      const requestUrl = new URL(axios.getUri(config), window.location.origin);
      shouldAttachCorrelationHeaders =
        requestUrl.origin === API_BASE_URL.origin &&
        hasPathPrefix(requestUrl.pathname, API_BASE_URL.pathname);
    } catch {
      shouldAttachCorrelationHeaders = url.startsWith(API_BASE);
    }
  } else {
    shouldAttachCorrelationHeaders = url.startsWith(API_BASE);
  }

  if (shouldAttachCorrelationHeaders) {
    config.headers = config.headers ?? {};
    Object.assign(config.headers, correlationHeaders());
  }
  return config;
});

axios.interceptors.response.use(
  (response) => {
    syncCorrelationIdFromAxiosHeaders(response.headers);
    return response;
  },
  (error) => {
    const h = error.response?.headers;
    if (h) {
      syncCorrelationIdFromAxiosHeaders(h);
    }
    return Promise.reject(error);
  }
);
